import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { RankingBadge } from '../RankingBadge';
import type { EncounterSummary } from '../../api/types';
import { formatDateTime, formatDecimal, formatDuration, formatNumber } from '../../utils/format';
import { serverBossMetricPath } from '../../utils/siteContext';

export function ReportEncounterTable({
  title,
  encounters,
  activeServerSlug,
  emptyMessage,
  footer,
}: {
  title: string;
  encounters: EncounterSummary[];
  activeServerSlug: string;
  emptyMessage: string;
  footer?: ReactNode;
}) {
  return (
    <section className="report-table-section">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <h2 className="h5 mb-0">{title}</h2>
        {footer}
      </div>
      <div className="card report-content-card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-dark table-sm table-hover align-middle report-detail-table mb-0">
              <thead>
                <tr>
                  <th>Encounter</th>
                  <th>Zone</th>
                  <th>Boss</th>
                  <th>Rank</th>
                  <th className="text-end">Duration</th>
                  <th className="text-end">Damage</th>
                  <th className="text-end">DPS</th>
                  <th className="text-end">Healing</th>
                  <th className="text-end">HPS</th>
                  <th className="text-end">POVs</th>
                  <th>Started</th>
                </tr>
              </thead>
              <tbody>
                {encounters.length === 0 ? (
                  <tr><td colSpan={11} className="text-body-secondary py-3">{emptyMessage}</td></tr>
                ) : encounters.map((encounter) => (
                  <tr key={encounter.id}>
                    <td>
                      <Link className="link-light fw-semibold text-decoration-none" to={`/encounters/${encounter.id}`}>{encounter.targetName}</Link>
                      <div className="small text-body-secondary">{encounter.result}</div>
                    </td>
                    <td>{encounter.zone}</td>
                    <td>
                      {encounter.raidBoss ? (
                        <Link className="link-light text-decoration-none" to={serverBossMetricPath(activeServerSlug, encounter.raidBoss.slug ?? String(encounter.raidBoss.id), 'DAMAGE')}>
                          {encounter.raidBoss.name}
                        </Link>
                      ) : <span className="text-body-secondary">Unmatched</span>}
                    </td>
                    <td><RankingBadge eligible={encounter.rankingEligible} reason={encounter.rankingIneligibleReason} /></td>
                    <td className="text-end tabular-nums">{formatDuration(encounter.durationSec)}</td>
                    <td className="text-end tabular-nums">{formatNumber(encounter.totalDamage)}</td>
                    <td className="text-end tabular-nums">{formatDecimal(encounter.dps, 1)}</td>
                    <td className="text-end tabular-nums">{formatNumber(encounter.totalHealing ?? 0)}</td>
                    <td className="text-end tabular-nums">{formatDecimal(encounter.hps ?? 0, 1)}</td>
                    <td className="text-end tabular-nums">{encounter.contributorCount}</td>
                    <td className="small text-body-secondary text-nowrap">{formatDateTime(encounter.startedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}
